import { QrCode, Star, Instagram, BarChart3 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import serviceMappingImg from "@/assets/service-mapping.jpg";
import serviceOptimizationImg from "@/assets/service-optimization.jpg";
import serviceTrackingImg from "@/assets/service-tracking.jpg";
import serviceTrainingImg from "@/assets/service-training.jpg";

const Services = () => {
  const { language } = useLanguage();
  
  const services = [
    {
      icon: QrCode,
      title: language === "fr" ? "QR Code + Display physique" : "QR Code + Physical Display",
      description: language === "fr" 
        ? "Un QR code aux couleurs de votre marque + un compteur social media live qui attire l'œil et déclenche la preuve sociale."
        : "A branded QR code + live social media counter that catches the eye and triggers social proof.",
      image: serviceMappingImg,
      gradient: "from-rose-500 to-pink-600",
    },
    {
      icon: Star,
      title: language === "fr" ? "Avis Google simplifiés" : "Simplified Google Reviews",
      description: language === "fr"
        ? "Place ID préchargé, texte d'avis suggéré avec fonction Copier. Le client publie en 30 secondes au lieu de 5 minutes."
        : "Pre-loaded Place ID, suggested review text with Copy function. Customer posts in 30 seconds instead of 5 minutes.",
      image: serviceOptimizationImg,
      gradient: "from-pink-500 to-rose-600",
    },
    {
      icon: Instagram,
      title: language === "fr" ? "Ouverture directe Instagram" : "Direct Instagram Opening",
      description: language === "fr"
        ? "Ouverture directe du profil Instagram dans l'app — pas de redirection, pas de recherche. Le follow devient évident."
        : "Direct opening of Instagram profile in the app — no redirects, no searching. Following becomes obvious.",
      image: serviceTrackingImg,
      gradient: "from-rose-400 to-pink-500",
    },
    {
      icon: BarChart3,
      title: language === "fr" ? "Métriques honnêtes" : "Honest Metrics",
      description: language === "fr"
        ? "On mesure les intentions (clics vers Google, ouvertures Instagram) — pas les actions qu'on ne contrôle pas."
        : "We measure intentions (clicks to Google, Instagram opens) — not actions we can't control.",
      image: serviceTrainingImg,
      gradient: "from-pink-400 to-rose-500",
    },
  ];

  return (
    <section id="services" className="py-12 lg:py-16 bg-slate-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-100 mb-3">
            <span className="text-xs font-medium text-violet-600">
              {language === "fr" ? "🎯 Conçu pour convertir, pas pour impressionner" : "🎯 Built to convert, not to impress"}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-slate-800 mb-2">
            {language === "fr" ? "Chaque détail est pensé pour l'action" : "Every detail is designed for action"}
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto text-sm">
            {language === "fr" 
              ? "Moins de friction, plus de psychologie : on guide vos clients vers l'engagement en quelques secondes."
              : "Less friction, more psychology: we guide your customers to engage in just seconds."}
          </p>
        </div>

        {/* Bento grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-slate-100"
            >
              {/* Glass shine effect - subtle */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              {/* Image container */}
              <div className="relative h-40 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${service.gradient} opacity-30`} />
                
                {/* Icon badge */}
                <div className={`absolute top-4 left-4 w-10 h-10 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg`}>
                  <service.icon className="h-5 w-5 text-white" />
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5">
                <h3 className="text-lg font-display font-semibold text-slate-800 mb-2">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
